import type { Notification } from '@elara/client-core/models/NotificationModel';

import type { IconName } from '#ui/MaterialIcon.js';

import { useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { requestNotificationPermission } from '#lib/web-notifications.js';
import { useNavigate, useNotificationsState, useTripsState } from '#states/hooks.js';
import { AppImage } from '#ui/AppImage.js';
import { Button } from '#ui/button.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { Popover, PopoverContent, PopoverTrigger } from '#ui/popover.js';
import { ScrollArea } from '#ui/scroll-area.js';

const TYPE_ICONS: Record<string, IconName> = {
  trip: 'luggage',
  booking: 'local_activity',
  account: 'person',
  feature: 'stars',
};

interface NotificationsBellProps {
  displayLabel: boolean;
}

export function NotificationsBell({ displayLabel }: NotificationsBellProps) {
  const notificationsState = useNotificationsState();
  const tripsState = useTripsState();
  const { navigate } = useNavigate();

  const [showUnreadOnly, setShowUnreadOnly] = useState(true);
  const visibleItems = showUnreadOnly
    ? notificationsState.items.filter((n: Notification) => !n.read)
    : notificationsState.items;

  const handleBellClick = () => {
    // Request permission on user gesture if not yet granted, then initialize FCM
    if (globalThis.Notification !== undefined && globalThis.Notification.permission === 'default') {
      void requestNotificationPermission().then(async (permission) => {
        if (permission === 'granted') {
          await notificationsState.initFCM();
        }
      });
    }
  };

  const handleNotificationClick = (notificationId: string, url: string) => {
    void notificationsState.markRead(notificationId);
    notificationsState.close();
    navigate(url);
  };

  return (
    <Popover
      open={notificationsState.isOpen}
      onOpenChange={(open) => {
        notificationsState.isOpen = open;
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant={displayLabel ? 'sidebar' : 'icon'}
          size={displayLabel ? 'sidebar' : 'icon'}
          className={cn('relative', displayLabel && 'w-full')}
          onClick={handleBellClick}
          tooltip={!displayLabel ? 'Notifications' : undefined}
          tooltipPosition="right"
        >
          <MaterialIcon
            icon="notifications"
            filled={notificationsState.isOpen}
            className={cn(notificationsState.isOpen && 'text-accent')}
          />
          {notificationsState.hasUnread && (
            <span
              className={cn('absolute h-2 w-2 rounded-full bg-accent', displayLabel ? 'top-2 left-6' : 'top-2 right-2')}
            />
          )}
          {displayLabel && (
            <span
              className={cn('absolute left-10 top-0 bottom-0 flex items-center overflow-hidden pointer-events-none')}
            >
              <span className="pl-2 whitespace-nowrap">Notifications</span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side={tripsState.isMobile ? 'bottom' : 'right'}
        align="end"
        sideOffset={tripsState.isMobile ? 8 : 16}
        collisionPadding={16}
        className="w-[calc(100vw-2rem)] max-w-96 p-0 overflow-hidden"
      >
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <div className="flex items-center gap-1">
            {(['unread', 'all'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                className={cn(
                  'px-2.5 py-1 text-xs rounded-sm transition-colors focus:outline-none',
                  (tab === 'unread') === showUnreadOnly
                    ? 'bg-secondary text-foreground font-medium'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                onClick={() => setShowUnreadOnly(tab === 'unread')}
              >
                {tab === 'all' ? 'All' : 'New'}
              </button>
            ))}
          </div>
          {notificationsState.hasUnread && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors focus:outline-none"
              onClick={() => void notificationsState.markAllRead()}
            >
              Mark all as read
            </button>
          )}
        </div>
        <ScrollArea className="max-h-96 overflow-y-auto">
          {visibleItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
              <MaterialIcon icon="celebration" size={IconSizes['2xl']} weight="light" className="mb-2" />
              <p className="text-sm">{showUnreadOnly ? 'No new notifications' : 'No notifications'}</p>
            </div>
          ) : (
            <div className="flex flex-col">
              {visibleItems.map((notification: Notification) => (
                <button
                  type="button"
                  key={notification.id}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-background-secondary cursor-pointer border-b last:border-b-0 focus:outline-none',
                    !notification.read && 'bg-background-secondary',
                  )}
                  onClick={() => handleNotificationClick(notification.id, notification.url)}
                >
                  <div className="relative">
                    {notification.imageUrl ? (
                      <AppImage src={notification.imageUrl} alt="" className="h-10 w-10 shrink-0 rounded-sm object-cover" />
                    ) : (
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-muted">
                        <MaterialIcon
                          icon={TYPE_ICONS[notification.type] ?? 'notifications'}
                          className="text-base text-muted-foreground"
                        />
                      </span>
                    )}
                    {!notification.read && (
                      <span
                        className={cn(
                          'absolute -top-1 -right-1 h-3 w-3 border-2 border-background-secondary shrink-0 rounded-full bg-accent',
                        )}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 mt-0.5">
                    <p className={cn('text-sm leading-tight font-medium')}>{notification.title}</p>
                    {notification.body && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.body}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
